import { useState, useRef, useCallback, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { AnalyticsEvent, InlineEditResolvedAction } from '@/analytics/schema/events.schema'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'
import { applyMarkdownToEditor, insertMarkdownAtPosition, serializeEditorToMarkdown } from '@/artifact-editor/utils/editor-helpers'
import { replaceCitationsWithSpans } from '@/artifact-editor/utils/citation-helpers'
import type { ReferenceInfoByCitationId } from '@/artifact-editor/utils/citation-helpers'
import { EDIT_PRESETS } from '@/inline-edit/constants/edit-presets'
import { kHighlightClassPending } from '@/inline-edit/constants/highlight'
import type { EditorView } from 'prosemirror-view'

type UseArtifactDiffReviewProps = {
  conversationId: string
  artifactVersionId: string
  sessionId: string
  referenceInfoByCitationId: ReferenceInfoByCitationId
  editorPanelRef: React.RefObject<HTMLDivElement | null>
  trackEvent: TrackEventFn
  // From useArtifactAutoSave
  performSave: (content: string, versionId: string) => Promise<void>
  setEditedContent: (content: string | null) => void
  pendingContent: React.MutableRefObject<string | null>
  lastSavedContent: React.MutableRefObject<string | null>
  // From useDiffReview
  isReviewingDiff: boolean
  diffOldMarkdown: string
  diffNewMarkdown: string
  clearDiffReview: () => void
  clearDiffReviewOnReject: () => void
  // From useInlineEdit
  getEditorView: () => EditorView | null
  selectionRange: { from: number; to: number } | null
  inlineEditSelection: { text: string; from: number; to: number } | null
  submitInlineEdit: (instruction: string) => void
  dismissInlineEdit: () => void
  onReviewComplete: () => void
  onInsertComplete: (range: { from: number; to: number }) => void
  highlightDecoration: { className: string; from: number; to: number } | null
  hasPendingResult: boolean
}

export function useArtifactDiffReview(props: UseArtifactDiffReviewProps) {
  const {
    conversationId,
    artifactVersionId,
    sessionId,
    referenceInfoByCitationId,
    editorPanelRef,
    trackEvent,
    performSave,
    setEditedContent,
    pendingContent,
    lastSavedContent,
    isReviewingDiff,
    diffOldMarkdown,
    diffNewMarkdown,
    clearDiffReview,
    clearDiffReviewOnReject,
    getEditorView,
    selectionRange,
    inlineEditSelection,
    submitInlineEdit,
    dismissInlineEdit,
    onReviewComplete,
    onInsertComplete,
    highlightDecoration,
    hasPendingResult,
  } = props

  // ── Inline edit tracking refs ──

  const highlightScrollOffsetRef = useRef<number | null>(null)
  const inlineEditId = useRef<string | null>(null)
  const inlineEditSubmittedAt = useRef<number | null>(null)
  const inlineEditCompletedAt = useRef<number | null>(null)

  // ── Helpers ──

  const findScrollContainer = (): HTMLElement | null => {
    let el: HTMLElement | null = editorPanelRef.current?.parentElement ?? null
    while (el && getComputedStyle(el).overflowY !== 'auto') {
      el = el.parentElement
    }
    return el
  }

  // ── Handlers ──

  const handleInlineEditSubmitWithSave = useCallback(
    async (instruction: string) => {
      if (!artifactVersionId) return
      try {
        if (pendingContent.current && pendingContent.current !== lastSavedContent.current) {
          await performSave(pendingContent.current, artifactVersionId)
        }
        inlineEditId.current = uuidv4()
        inlineEditSubmittedAt.current = Date.now()
        const selectionText = inlineEditSelection?.text ?? ''
        trackEvent(AnalyticsEvent.InlineEditSubmitted, {
          inlineEditId: inlineEditId.current,
          artifactVersionId,
          conversationId,
          sessionId,
          selectionWordCount: selectionText.trim().split(/\s+/).filter(Boolean).length,
          selectionFrom: inlineEditSelection?.from ?? 0,
          selectionTo: inlineEditSelection?.to ?? 0,
          isPreset: EDIT_PRESETS.some((p) => p.instruction === instruction),
        })
        submitInlineEdit(instruction)
      } catch (error) {
        console.error('Failed to save before inline edit:', error)
      }
    },
    [artifactVersionId, performSave, submitInlineEdit, conversationId, sessionId, trackEvent, inlineEditSelection, pendingContent, lastSavedContent]
  )

  const trackInlineEditResolved = useCallback(
    (action: InlineEditResolvedAction) => {
      if (!inlineEditId.current) return
      trackEvent(AnalyticsEvent.InlineEditResolved, {
        inlineEditId: inlineEditId.current,
        artifactVersionId: artifactVersionId ?? '',
        conversationId,
        sessionId,
        inlineEditResolution: action,
      })
      inlineEditId.current = null
      inlineEditSubmittedAt.current = null
      inlineEditCompletedAt.current = null
    },
    [artifactVersionId, conversationId, sessionId, trackEvent]
  )

  const handleRejectDiff = useCallback(() => {
    trackInlineEditResolved(InlineEditResolvedAction.Declined)
    clearDiffReviewOnReject()
    onReviewComplete()
  }, [trackInlineEditResolved, clearDiffReviewOnReject, onReviewComplete])

  const handleAcceptDiff = useCallback(
    async (resolvedMarkdown?: string) => {
      trackInlineEditResolved(diffOldMarkdown === diffNewMarkdown ? InlineEditResolvedAction.NoDiff : InlineEditResolvedAction.Replaced)
      const view = getEditorView()
      const content = resolvedMarkdown ?? diffNewMarkdown
      if (view && content) {
        const preprocessedMd = replaceCitationsWithSpans(content, referenceInfoByCitationId)
        applyMarkdownToEditor(view, preprocessedMd)
      }
      const scrollEl = findScrollContainer()
      if (scrollEl && highlightScrollOffsetRef.current !== null) {
        scrollEl.scrollTo({ top: highlightScrollOffsetRef.current - 60, behavior: 'smooth' })
      }
      clearDiffReview()
      onReviewComplete()
      if (content && artifactVersionId) {
        pendingContent.current = content
        setEditedContent(content)
        await performSave(content, artifactVersionId)
      }
    },
    [
      trackInlineEditResolved,
      getEditorView,
      diffOldMarkdown,
      diffNewMarkdown,
      referenceInfoByCitationId,
      clearDiffReview,
      onReviewComplete,
      artifactVersionId,
      performSave,
      pendingContent,
      setEditedContent,
    ]
  )

  const handleInsertBelow = useCallback(
    async (insertionMarkdown: string) => {
      trackInlineEditResolved(InlineEditResolvedAction.InsertedBelow)
      const view = getEditorView()
      if (!view || !selectionRange) return
      const preprocessedMd = replaceCitationsWithSpans(insertionMarkdown, referenceInfoByCitationId)
      const insertedRange = insertMarkdownAtPosition(view, preprocessedMd, selectionRange.to)
      // Ordering matters: onInsertComplete must be LAST so resultHighlight isn't cleared by dismiss
      clearDiffReviewOnReject()
      onReviewComplete()
      dismissInlineEdit()
      onInsertComplete(insertedRange)
      const scrollEl = findScrollContainer()
      if (scrollEl && highlightScrollOffsetRef.current !== null) {
        scrollEl.scrollTo({ top: highlightScrollOffsetRef.current - 60, behavior: 'smooth' })
      }
      if (artifactVersionId) {
        const newMarkdown = serializeEditorToMarkdown(view)
        pendingContent.current = newMarkdown
        setEditedContent(newMarkdown)
        await performSave(newMarkdown, artifactVersionId)
      }
    },
    [
      trackInlineEditResolved,
      getEditorView,
      selectionRange,
      referenceInfoByCitationId,
      clearDiffReviewOnReject,
      onReviewComplete,
      dismissInlineEdit,
      onInsertComplete,
      artifactVersionId,
      performSave,
      pendingContent,
      setEditedContent,
    ]
  )

  // ── Highlight visibility tracking ──

  const [highlightVisibility, setHighlightVisibility] = useState<{
    direction: 'above' | 'below'
    scrollContainerRect: DOMRect
  } | null>(null)

  useEffect(() => {
    if (!isReviewingDiff || !highlightDecoration) {
      setHighlightVisibility(null)
      highlightScrollOffsetRef.current = null
      return
    }
    const view = getEditorView()
    if (!view) return

    const scrollEl = findScrollContainer()
    if (!scrollEl) return

    const firstHighlight = view.dom.querySelector(`.${kHighlightClassPending}`) as HTMLElement | null
    if (!firstHighlight) return
    const highlightScreenY = firstHighlight.getBoundingClientRect().top
    const scrollScreenY = scrollEl.getBoundingClientRect().top
    const highlightDocY = highlightScreenY - scrollScreenY + scrollEl.scrollTop
    highlightScrollOffsetRef.current = highlightDocY

    const BUFFER = 100

    const checkVisibility = () => {
      const scrollRect = scrollEl.getBoundingClientRect()
      const visibleTop = scrollEl.scrollTop

      if (highlightDocY < visibleTop) {
        setHighlightVisibility({ direction: 'above', scrollContainerRect: scrollRect })
      } else if (highlightDocY > visibleTop + BUFFER) {
        setHighlightVisibility({ direction: 'below', scrollContainerRect: scrollRect })
      } else {
        setHighlightVisibility(null)
      }
    }
    checkVisibility()
    scrollEl.addEventListener('scroll', checkVisibility)
    return () => scrollEl.removeEventListener('scroll', checkVisibility)
    // getEditorView and findScrollContainer are stable ref-based getters — excluded intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewingDiff, highlightDecoration])

  const scrollToHighlight = useCallback(() => {
    if (highlightScrollOffsetRef.current === null) return
    const scrollEl = findScrollContainer()
    if (!scrollEl) return
    scrollEl.scrollTo({ top: highlightScrollOffsetRef.current - 60, behavior: 'smooth' })
  }, [])

  // ── InlineEditCompleted analytics ──

  useEffect(() => {
    if (hasPendingResult && inlineEditSubmittedAt.current && !inlineEditCompletedAt.current) {
      inlineEditCompletedAt.current = Date.now()
      trackEvent(AnalyticsEvent.InlineEditCompleted, {
        inlineEditId: inlineEditId.current ?? '',
        artifactVersionId: artifactVersionId ?? '',
        conversationId,
        sessionId,
        inlineEditSuccess: true,
        durationMs: inlineEditCompletedAt.current - inlineEditSubmittedAt.current,
      })
    }
  }, [hasPendingResult, artifactVersionId, conversationId, sessionId, trackEvent])

  return {
    handleAcceptDiff,
    handleRejectDiff,
    handleInsertBelow,
    handleInlineEditSubmitWithSave,
    highlightVisibility,
    scrollToHighlight,
  }
}
